import { useState } from 'react';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { isDateStringEmpty } from '../../../sharedComponents/dateHelper';
import { AccessoryType, CollectedAccessories } from '../accessory.types';
import { ServiceOrder, ServiceOrderStatus } from '../serviceOrder.types';
import { getObjectKeyForAccessory } from '../serviceOrderUtils';
import {
    DateParseErrors,
    ExecutionDateErrorType,
    initialDateParseErrors,
    ServiceOrderDateField,
    ServiceOrderFormFieldError,
    validateCompletedAt,
    validateStartedAt,
} from '../shared/ServiceOrderFormValidation';
import { useServiceOrderExecutionUpdate } from '../shared/useServiceOrderExecutionUpdate';

export type ServiceOrderExecutionDetailsFormData = {
    accessories: CollectedAccessories;
    generalInformation?: string;
    startedAt: Date | undefined;
    completedAt: Date | undefined;
};

type AccessoryTypeErrorType = 'invalidValue';
export type ServiceOrderExecutionFormErrors = {
    [key in AccessoryType]?: ServiceOrderFormFieldError<AccessoryTypeErrorType>;
} & {
    startedAt?: ServiceOrderFormFieldError<ExecutionDateErrorType>;
    completedAt?: ServiceOrderFormFieldError<ExecutionDateErrorType>;
};

export const useServiceOrderExecutionForm = (
    selectedServiceOrder: ServiceOrder | undefined,
    closeSidebar: () => void,
) => {
    const { updateExecution, isLoading } = useServiceOrderExecutionUpdate();

    const [formErrors, setFormErrors] = useState<ServiceOrderExecutionFormErrors>({});
    const [formData, setFormData] = useState<ServiceOrderExecutionDetailsFormData>({
        accessories: selectedServiceOrder?.vehicle.collectedAccessories ?? {},
        generalInformation: selectedServiceOrder?.generalInformation,
        startedAt: selectedServiceOrder?.startedAt,
        completedAt: selectedServiceOrder?.completedAt,
    });
    const [dateParseErrors, setDateParseErrors] = useState<DateParseErrors>(initialDateParseErrors);

    const setDateParseError = (field: ServiceOrderDateField, hasParseError: boolean) => {
        setDateParseErrors((current) => ({ ...current, [field]: hasParseError }));
    };

    const validateForm = (): boolean => {
        if (selectedServiceOrder === undefined) {
            return false;
        }

        const newFormErrors: ServiceOrderExecutionFormErrors = {};

        const withoutCompletionDate =
            isDateStringEmpty(formData.completedAt) && selectedServiceOrder?.completedAt === undefined;

        const allAccessoriesAreValid =
            withoutCompletionDate || validateAccessoriesRequired(formData, selectedServiceOrder, newFormErrors);

        newFormErrors.startedAt = validateStartedAt(formData.startedAt, selectedServiceOrder.startedAt);
        newFormErrors.completedAt = validateCompletedAt(
            formData.startedAt,
            formData.completedAt,
            selectedServiceOrder.status === ServiceOrderStatus.COMPLETED,
        );

        setFormErrors(newFormErrors);
        const hasParseError = Object.values(dateParseErrors).some(Boolean);
        const hasBusinessError = Object.values(newFormErrors).some((error) => error !== undefined);

        return allAccessoriesAreValid && !hasParseError && !hasBusinessError;
    };

    const handleSaveServiceOrder = runInBackgroundCallback(async () => {
        if (!validateForm() || selectedServiceOrder === undefined) {
            return;
        }

        try {
            const successMessageId = await updateExecution({
                orderIds: [selectedServiceOrder.id],
                startedAt: formData.startedAt,
                completedAt: formData.completedAt,
                generalInformation: formData.generalInformation,
                collectedAccessories: formData.accessories,
            });

            showSuccessNotification(successMessageId);
            closeSidebar();
        } catch (error) {
            handleQueryError(error);
        }
    });

    return {
        formErrors,
        executionDetails: formData,
        setExecutionDetails: setFormData,
        setDateParseError,
        hasDateParseError: Object.values(dateParseErrors).some(Boolean),
        handleSaveServiceOrder,
        isExecutionDetailsLoading: isLoading,
    };
};

export const validateAccessoriesRequired = (
    executionDetails: ServiceOrderExecutionDetailsFormData,
    selectedServiceOrder: ServiceOrder,
    newFormErrors: ServiceOrderExecutionFormErrors,
) => {
    let allAccessoriesAreValid = true;
    selectedServiceOrder.vehicle.requestedAccessories.forEach((requestedAccessory: AccessoryType) => {
        const accessoryValue = executionDetails.accessories[getObjectKeyForAccessory(requestedAccessory)];
        if (accessoryValue === undefined || accessoryValue < 0) {
            newFormErrors[requestedAccessory] = { type: 'invalidValue' };
            allAccessoriesAreValid = false;
        }
    });

    return allAccessoriesAreValid;
};
